import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Loader2, BarChart3, Download, RefreshCw,
} from 'lucide-react';

import { Button, Card } from '../../ui-v2';
import { getResumenInformes, descargarCsvInformes } from '../api';

import {
  GraficoCitasMensuales,
  GraficoNoAsistio,
  GraficoTopTecnicos,
  GraficoDistribucionAmbito,
} from '../informes/GraficosInformes';

/**
 * Informes — analítica histórica del módulo.
 *
 * Layout:
 *   - Mini-header sticky con filtros temporales + Exportar CSV
 *   - KPIs del período
 *   - 2x2 grid de gráficos (recharts)
 *
 * Ruta: /citas-tecnicas/informes
 * Permiso: intervenciones.informes.view
 */

const RANGOS_PRESET = [
  { id: '3m', label: 'Últimos 3 meses', meses: 3 },
  { id: '6m', label: 'Últimos 6 meses', meses: 6 },
  { id: '12m', label: 'Últimos 12 meses', meses: 12 },
  { id: 'custom', label: 'Personalizado' },
];

export default function Informes() {
  const [rangoId, setRangoId] = useState('6m');
  const [customDesde, setCustomDesde] = useState('');
  const [customHasta, setCustomHasta] = useState('');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);

  // Calcular rango efectivo
  const rango = useMemo(() => {
    const hoy = new Date();
    if (rangoId === 'custom' && customDesde && customHasta) {
      return { desde: customDesde, hasta: customHasta };
    }
    const preset = RANGOS_PRESET.find((r) => r.id === rangoId);
    const meses = preset?.meses ?? 6;
    const desde = new Date(hoy);
    desde.setMonth(desde.getMonth() - meses);
    return {
      desde: desde.toISOString().slice(0, 10),
      hasta: hoy.toISOString().slice(0, 10),
    };
  }, [rangoId, customDesde, customHasta]);

  const cargar = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await getResumenInformes(rango);
      setData(res);
    } catch (e) {
      console.error('[Informes]', e);
      setError(e?.response?.data?.error || 'No se pudo cargar los informes.');
    } finally {
      setLoading(false);
    }
  }, [rango]);

  useEffect(() => { cargar(); }, [cargar]);

  async function onExportar() {
    setExporting(true);
    try {
      await descargarCsvInformes(rango);
    } catch (e) {
      console.error('[exportar]', e);
      alert(e?.response?.data?.error || 'No se pudo descargar el Excel.');
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="min-h-full">
      {/* MINI-HEADER STICKY */}
      <div className="sticky top-14 z-30 bg-white/95 backdrop-blur-sm border-b border-slate-200">
        <div className="px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <BarChart3 className="w-5 h-5 text-slate-400 shrink-0" />
            <h1 className="text-sm font-medium text-slate-900">Informes</h1>
            {data && (
              <div className="hidden md:flex items-center gap-3 text-xs text-slate-500 ml-3">
                <span>
                  <span className="text-slate-900 font-medium tabular-nums">
                    {data.kpis.total_citas}
                  </span>{' '}
                  citas en el período
                </span>
                {data.kpis.tasa_no_asistio_pct > 0 && (
                  <>
                    <span className="text-slate-300">·</span>
                    <span>
                      <span className="text-amber-600 font-medium tabular-nums">
                        {data.kpis.tasa_no_asistio_pct}%
                      </span> de "no asistió"
                    </span>
                  </>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={cargar}
              disabled={loading}
            >
              {loading ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <RefreshCw className="w-3.5 h-3.5" />
              )}
              Actualizar
            </Button>
            <Button
              size="sm"
              onClick={onExportar}
              disabled={exporting || !data || data.kpis.total_citas === 0}
            >
              {exporting ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Download className="w-3.5 h-3.5" />
              )}
              Exportar Excel
            </Button>
          </div>
        </div>
      </div>

      {/* CONTENIDO */}
      <div className="px-6 py-6">
        {/* Filtros temporales */}
        <Card className="p-4 mb-5">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-xs text-slate-500 uppercase tracking-wider">
              Período
            </span>
            <div className="flex gap-1.5 flex-wrap">
              {RANGOS_PRESET.map((r) => {
                const activo = rangoId === r.id;
                return (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => setRangoId(r.id)}
                    className={
                      'px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ' +
                      (activo
                        ? 'bg-primary text-white border-primary'
                        : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50')
                    }
                  >
                    {r.label}
                  </button>
                );
              })}
            </div>

            {rangoId === 'custom' && (
              <div className="flex items-center gap-2 ml-auto">
                <input
                  type="date"
                  value={customDesde}
                  onChange={(e) => setCustomDesde(e.target.value)}
                  className="border border-slate-200 rounded-md px-2.5 py-1.5 text-xs bg-white"
                />
                <span className="text-xs text-slate-400">a</span>
                <input
                  type="date"
                  value={customHasta}
                  onChange={(e) => setCustomHasta(e.target.value)}
                  className="border border-slate-200 rounded-md px-2.5 py-1.5 text-xs bg-white"
                />
              </div>
            )}

            {data && (
              <span className="text-xs text-slate-400 ml-auto tabular-nums">
                {data.rango.desde} → {data.rango.hasta}
              </span>
            )}
          </div>
        </Card>

        {error && (
          <div className="mb-5 border border-red-200 bg-red-50 rounded-lg p-3 text-red-700 text-sm">
            {error}
          </div>
        )}

        {loading && !data ? (
          <div className="py-16 text-center text-sm text-slate-500">
            <Loader2 className="w-5 h-5 inline-block animate-spin mr-2" />
            Cargando informes…
          </div>
        ) : data ? (
          <>
            {/* KPIs */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
              <KpiCard
                label="Total citas"
                value={data.kpis.total_citas}
                colorVar="default"
              />
              <KpiCard
                label="Realizadas"
                value={data.kpis.realizadas}
                colorVar="primary"
              />
              <KpiCard
                label="No asistió"
                value={data.kpis.no_asistio}
                hint={`${data.kpis.tasa_no_asistio_pct}% del total terminado`}
                colorVar="warning"
              />
              <KpiCard
                label="Canceladas"
                value={data.kpis.canceladas}
                hint={`${data.kpis.tasa_cancelacion_pct}% del total`}
                colorVar="muted"
              />
            </div>

            {/* Gráficos en grid 2×2 (apilan en pantallas estrechas) */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <GraficoCitasMensuales data={data.citas_por_mes} />
              <GraficoNoAsistio data={data.tasa_no_asistio_por_mes} />
              <GraficoTopTecnicos data={data.top_tecnicos} />
              <GraficoDistribucionAmbito data={data.distribucion_ambito} />
            </div>

            <div className="text-[11px] text-slate-400 text-center mt-8">
              Datos del período {data.rango.desde} al {data.rango.hasta}.
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

function KpiCard({ label, value, hint, colorVar }) {
  const colorClass =
    colorVar === 'primary' ? 'text-primary' :
    colorVar === 'warning' ? 'text-amber-600' :
    colorVar === 'muted' ? 'text-slate-500' :
    'text-slate-900';
  return (
    <Card className="p-4">
      <div className="text-[11px] text-slate-500 uppercase tracking-wider">{label}</div>
      <div className={`text-3xl font-medium mt-1.5 tabular-nums leading-none ${colorClass}`}>
        {value}
      </div>
      {hint && (
        <div className="text-[11px] text-slate-400 mt-1.5">{hint}</div>
      )}
    </Card>
  );
}
